import { StatusForCoach } from './Coach/ParticipationListItem';
import { ConversionCoachStatus } from './Enums/ConversionCoachStatus';
import { ConversionStatusCode } from './Enums/ConversionStatusCode';
import { DecisionsOnPackages } from './Enums/DecisionOnPackages';
import { SystemUser } from './global';

export interface ConversionProjectViewModel {
  companyInformation: CompanyInformationTexts;
  unionInformation: UnionInformationTexts;
  projectInformation: ProjectInformationTexts;
  conversionAdvisorInformation: ConversionAdvisorInformationTexts;
  searchBoxPlaceholder: string;
  newParticipantsListHeadline: string;
  ongoingParticipantsListHeadline: string;
  finishedParticipantsListHeadline: string;
  assignCoachBtn: string;
  changeCoachBtn: string;
  assignCoachErrorMsg: string;
  missingCoachErrorMsg: string;
  coachListDefaultValue: string;
}

export interface CompanyInformationTexts {
  headline: string;
  name: string;
  worksiteName: string;
  orgNumber: string;
  address: string;
  mailingAddress: string;
  county: string;
  contactPerson: string;
  telephone: string;
  email: string;
}

export interface UnionInformationTexts {
  headline: string;
  name: string;
  contactPerson: string;
  telephone: string;
  email: string;
}

export interface ProjectInformationTexts {
  headline: string;
  name: string;
  accountBankruptcyDate: string;
  startDate: string;
  frameworkAgreement: string;
  supplierName: string;
  amountAssignedParticipants: string;
  otherInformation: string;
}

export interface ConversionAdvisorInformationTexts {
  headline: string;
  name: string;
  telephone: string;
  email: string;
  address: string;
}

export interface ConversionProjectDetails {
  name: string;
  city: string;
  county: string;
  accountBankruptcyDate: string;
  projectStartDate: Date;
  worksiteOrganization: worksiteOrganization;
  worksiteContact: Contact;
  unionOrganization: unionOrganization;
  unionContact: Contact;
  participantCount: string;
  coachStartDate: string;
  subcontractorsNames: string[];
  frameworkAgreement: string;
  companyContactPerson: string;
  companyContactPersonTelephone: string;
  companyContactPersonEmail: string;
  systemUser: SystemUser;
  ovrigInfoTillLeverantor: string;
}

interface Contact {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
}

interface worksiteOrganization {
  organisationName: string;
  organisationNumber: string;
  address: string;
  postalCode: string;
  city: string;
  postalAddress: string;
  postalAddressPostalCode: string;
  postalCity: string;
}

interface unionOrganization {
  organisationName: string;
}

export interface ConversionProjectListItem {
  id?: string;
  leverantorId?: string;
  additionalInformationForSupplier: string;
  ansokanNumber: string;
  projectName: string;
  projectNumber: string;
  city: string;
  county: string;
  frameworkAgreement: string;
  worksiteOrganizationName: string;
  worksiteContactFullname: string;
  worksiteContactPhone: string;
  worksiteContactEmail: string;
  unionContactFullname: string;
  unionContactPhone: string;
  unionContactEmail: string;
  unionOrganizationName: string;
  systemUser: SystemUser;
  coachStatus?: ConversionCoachStatus;
  coachStatusText?: string | null;
  participantCount?: number;
  statusCode?: ConversionStatusCode;
  statusCodeText?: string | null;
  projectStartDate?: Date;
  projectEndDate?: Date;
}

export interface ConversionProjectParticipant {
  id?: string;
  conversionProjectId?: string;
  ansokanId: string;
  ansokanNumber: string;
  projectName: string;
  participantFullname: string;
  participantPersonalNumber: string;
  participantPhone: string;
  participantMobilePhone: string;
  participantEmail: string;
  participantAddress: string;
  participantPostalCode: string;
  participantCity: string;
  coachId?: string;
  coachFullname: string;
  participantDeltagarId: string;
  statusForCoach?: StatusForCoach;
  statusForCoachText?: string | null;
  decisionOnPackage?: DecisionsOnPackages[];
  decisionOnPackageText?: string | null;
  protectedIdentity?: boolean;
  participantStartDate?: Date;
  participantEndDate?: Date;
  deltagarId: string;
  supportPackage?: number;
  status?: number;
  startDate?: Date;
  city: string;
  frameworkAgreement: string;
  otherInfoToCoach: string;
  meetingsCount?: number;
}

export const ConversionStatusCodeText: {
  [key in ConversionStatusCode]: string;
} = {
  [ConversionStatusCode.NewProject]: 'Nytt projekt',
  [ConversionStatusCode.OnGoing]: 'Pågående',
  [ConversionStatusCode.EndReported]: 'Avslutat',
};

export const ConversionCoachStatusText: {
  [key in ConversionCoachStatus]: string;
} = {
  [ConversionCoachStatus.CoachNotAssigned]: 'Coach ej tilldelad',
  [ConversionCoachStatus.CoachAssigned]: 'Coach tilldelad',
};

export const DecisionsOnPackagesText: { [key in DecisionsOnPackages]: string } =
  {
    [DecisionsOnPackages.Paket1]: 'Paket 1, 12 månader',
    [DecisionsOnPackages.Paket2]: 'Paket 2, 2 månader',
    [DecisionsOnPackages.Tillaggspaket]: 'Nej',
    [DecisionsOnPackages.AnnanInsats]: 'Ja',
    [DecisionsOnPackages.Paket22]: 'Nej',
    [DecisionsOnPackages.TwelveMonths]: '12 mån stöd på plats',
    [DecisionsOnPackages.TwoMonths]: '2 mån stöd på plats',
    [DecisionsOnPackages.TwoMonthsDistans]: '2 mån stöd digitalt',
    [DecisionsOnPackages.TenMonthsDistans]: '10 månader digitalt',
    [DecisionsOnPackages.TenMonths]: '10 månader på plats',
    [DecisionsOnPackages.HealthTalk]: 'Hälsosamtal',
    [DecisionsOnPackages.GuidanceCall]: 'Vägledningssamtal',
    [DecisionsOnPackages.NeedsAnalysis]: 'Behovsanalys',
    [DecisionsOnPackages.TeambasedMapping]: 'Teambaserad 2 komp',
    [DecisionsOnPackages.PsychologistSupport]: 'Psykologstöd',
    [DecisionsOnPackages.HealthAdaptedOneToFour]: 'Hälsoanpassat stöd 1-4 mån',
    [DecisionsOnPackages.HealthAdaptedFiveToEight]:
      'Hälsoanpassat stöd 5-8 mån',
    [DecisionsOnPackages.HealthAdaptedNineToTwelve]:
      'Hälsoanpassat stöd 9-12 mån',
  };
